
import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BehaviorSubject } from 'rxjs';
import {
    ActionItemModifier,
    ActionItemStyle,
    ButtonActionItem,
    ShActionItemComponent
} from '../../../../elements/components/action-item/action-item.component';
import { ShDroppanelSelectComponent } from '../../../../elements/components/droppanel-select/droppanel-select.component';

import { ShClickOutsideDirective } from '../../../../elements/directives/click-outside/click-outside.directive';
import { DropdownDirection } from '../../../../form-controls/entities/dropdown-direction.entity';
import { FormSelectOption } from '../../../../form-controls/entities/form-select-option.entity';
import { FormValue, FormValueState } from '../../../../form-controls/entities/form-value.entity';
import { MultiFormSelectOptions } from '../../../../form-controls/entities/multi-form-select-options.entity';
import { multiFormSelectOptionWrapper } from '../../../../form-controls/helpers/multi-form-select-options-wrapper-helper';
import { ColumnDef } from '../../../list-view/entities/column-definition.entity';

@Component({
    standalone: true,
    selector: 'sh-column-selection',
    templateUrl: './column-selection.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslateModule, ShDroppanelSelectComponent, ShActionItemComponent, ShClickOutsideDirective]
})
export class ColumnSelectionComponent {
    private readonly translateService: TranslateService = inject(TranslateService);

    openDropdown$ = new BehaviorSubject<boolean>(false);
    dropdownDirection = DropdownDirection.BelowLeft;

    columnSelectButton: ButtonActionItem = {
        eventListener: () => this.toggleDropdown(),
        label: this.translateService.instant('generic-overview.column-selection.action'),
        icon: 'sliders',
        style: ActionItemStyle.PureSquareIconButton,
        modifiers: [ActionItemModifier.Small]
    };

    columnOptions = input<FormSelectOption[]>([]);
    selectedColumns = input<ColumnDef<string, any>[]>([]);

    columnSelectionChanged = output<string[]>();

    options = computed(() => this.processColumnOptions(this.columnOptions(), this.selectedColumns()));
    initialSelectedValues = computed(() => this.columnsToInitialValues(this.selectedColumns()));

    private processColumnOptions(
        options: FormSelectOption[],
        selectedColumns: ColumnDef<string, any>[]
    ): MultiFormSelectOptions {
        const selectedColumnKeys = selectedColumns.map(selected => selected.toString());
        const processedOptions =
            selectedColumns.length > 1 ? options : this.disableSelectedOptions(options, selectedColumnKeys);

        return multiFormSelectOptionWrapper(processedOptions);
    }

    private disableSelectedOptions(options: FormSelectOption[], selectedColumnKeys: string[]): FormSelectOption[] {
        return options.map(option => {
            const disabled: boolean = selectedColumnKeys.includes(option.value);
            const disabledInfoMsg: string = disabled
                ? this.translateService.instant('generic-overview.column-selection.minimum-columns')
                : '';

            return {
                ...option,
                disabled,
                disabledInfoMsg
            };
        });
    }

    private columnsToInitialValues(selectedColumns: ColumnDef<string, any>[] = []): FormValue[] {
        return selectedColumns.map(column => new FormValue(column.toString()));
    }

    public onColumnSelectionChanged(formValues: FormValue<FormSelectOption>[]): void {
        const initialColumns = this.initialSelectedValues().map(formValue => formValue.value);

        const selectedColumns = formValues
            .filter(formValue => formValue.state === FormValueState.SET)
            .map(formValue => formValue.value.value);

        const hasChanged: boolean = JSON.stringify(initialColumns) !== JSON.stringify(selectedColumns);

        if (hasChanged) {
            this.columnSelectionChanged.emit(selectedColumns);
        }
    }

    public toggleDropdown(): void {
        this.openDropdown$.next(!this.openDropdown$.value);
    }

    public closeDropdown(): void {
        this.openDropdown$.next(false);
    }
}
